// ============================================================
// sheetBuilder.js — lays out the drawing sheets for the PDF export
//
// Every sheet is ONE SVG string in PDF points (0,0 = top-left of the
// paper). pdfWriter.js writes each one into a PDF page with svg2pdf, and
// the export preview shows the very same strings — so what you see in the
// preview is exactly what prints.
//
// Sheet 1   = header + floor plan + side strip (legend, notes, title block)
// Sheet 2+  = full equipment schedule, then any notes that didn't fit
// Two templates share this layout and only differ in theme/styling:
//   'presentation' — Modern Presentation (default): soft fills, item names on the plan
//   'blueprint'    — Technical Blueprint: crisp line work, EQ ref tags
// ============================================================

import { PAPER_SIZES } from './paper';
import { renderPlan } from './planRenderer';
import { buildEquipmentSchedule } from './equipmentData';
import { textWidth, fitText, wrapText, esc, ftIn } from './textMetrics';
import { computeBbox } from '../utils/shopGeometry';

const FONT = 'Helvetica, Arial, sans-serif';

// Brand palette (from the export spec)
const C = {
  navy: '#17283C', blue: '#2C6FAD', sky: '#7AB6F1', mist: '#F0F3F7', white: '#FFFFFF', slate: '#26384B',
};

export const DEFAULT_DISCLAIMER =
  'Conceptual equipment arrangement only. All dimensions and equipment clearances must be verified against '
  + 'manufacturer specifications, actual site conditions, applicable building codes, and installation '
  + 'requirements prior to construction or equipment installation.';

const VERIFY_NOTES = [
  'Verify lift locations against slab thickness, concrete strength and anchoring requirements.',
  'Verify ceiling height and overhead door clearances for vehicles at full lift height.',
  'Confirm electrical, compressed air and drain locations before installation.',
  'Confirm equipment footprints with the selected manufacturer\'s installation drawings.',
];

// ─── Templates ───────────────────────────────────────────────────────────
// theme = colours/weights handed to the plan renderer + sheet chrome
export const TEMPLATES = {
  blueprint: {
    label: 'Technical Blueprint',
    defaults: { showLabels: false, showRefs: true },
    theme: {
      paper: C.white, ink: C.slate, text: C.navy, muted: '#5B6B7C',
      grid: '#DCE4EE', roomFill: C.mist, roomLine: '#7C8A99',
      clearFill: 'rgba(44,111,173,0.06)', clearLine: '#9DB9D6',
      equip: C.slate, equipLine: 0.55, wall: C.navy, wallPt: 3.2, door: C.blue,
      dim: '#4A5B6D', dimText: C.navy, marker: C.blue, markerText: C.navy, markerR: 6, labelPt: 5.5,
      // sheet chrome
      frame: C.navy, rule: '#C9D3DE', panelFill: C.white, headFill: C.navy, tableHead: C.mist, zebra: '#F7F9FB', radius: 0,
    },
  },
  presentation: {
    label: 'Modern Presentation',
    defaults: { showLabels: true, showRefs: false },
    theme: {
      paper: C.white, ink: C.slate, text: C.navy, muted: '#66778A',
      grid: '#E4EBF3', roomFill: '#E8F1FB', roomLine: C.sky,
      clearFill: 'rgba(122,182,241,0.12)', clearLine: C.sky,
      equip: C.blue, equipLine: 0.6, wall: C.navy, wallPt: 4, door: C.blue,
      dim: '#7C8A99', dimText: C.slate, marker: C.blue, markerText: C.blue, markerR: 6, labelPt: 6,
      frame: '#D5DEE8', rule: '#DFE6EE', panelFill: C.mist, headFill: C.navy, tableHead: '#E3ECF6', zebra: '#F6F9FC', radius: 6,
    },
  },
};

// Everything the export settings panel controls. Persisted in the project.
export const DEFAULT_EXPORT_SETTINGS = {
  template: 'presentation',
  paper: 'letter',
  showGrid: false,
  showLabels: null,          // null = template default
  showRefs: null,
  showDimensions: true,
  showSchedule: true,
  showRoomDimensions: true,
  showTitleBlock: true,
  showSiteConditions: true,
  showNotes: true,
  showClearance: false,
  showSignature: false,
  greyscale: false,
  logoDataUrl: '',
};

export function resolveSettings(s = {}) {
  const merged = { ...DEFAULT_EXPORT_SETTINGS, ...s };
  const tpl = TEMPLATES[merged.template] || TEMPLATES.presentation;
  if (merged.showLabels == null) merged.showLabels = tpl.defaults.showLabels;
  if (merged.showRefs == null) merged.showRefs = tpl.defaults.showRefs;
  if (!PAPER_SIZES[merged.paper]) merged.paper = 'letter';
  if (!TEMPLATES[merged.template]) merged.template = 'presentation';
  return merged;
}

const f = (n) => Number(n).toFixed(2);
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const clean = (v) => String(v ?? '').trim();

// Text element helper
const T = (x, y, s, { size = 7, bold = false, fill = C.navy, anchor = 'start' } = {}) =>
  `<text x="${f(x)}" y="${f(y)}" font-family="${FONT}" font-size="${size}"${bold ? ' font-weight="bold"' : ''} fill="${fill}"${anchor !== 'start' ? ` text-anchor="${anchor}"` : ''}>${esc(s)}</text>`;
const R = (x, y, w, h, { fill = 'none', stroke = 'none', sw = 0.5, rx = 0 } = {}) =>
  `<rect x="${f(x)}" y="${f(y)}" width="${f(w)}" height="${f(h)}"${rx ? ` rx="${rx}"` : ''} fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`;
const L = (x1, y1, x2, y2, stroke, sw = 0.5) =>
  `<line x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}" stroke="${stroke}" stroke-width="${sw}"/>`;

// ─── Project facts used by the header + title block ──────────────────────
function projectFacts(project) {
  const { shop, doors = [], placed = [], exportForm: form = {} } = project;
  const bb = computeBbox(shop.vertices || []);
  const isRect = (shop.shapeType || 'rect') !== 'free';
  const area = Math.abs((shop.vertices || []).reduce((s, p, i, v) => {
    const q = v[(i + 1) % v.length];
    return s + p.x * q.y - q.x * p.y;
  }, 0) / 2);
  const items = placed.filter((p) => !p.isRoom);
  const rooms = placed.filter((p) => p.isRoom);
  const overhead = doors.filter((d) => d.type === 'overhead').length;
  const location = [form.address, form.city].map(clean).filter(Boolean).join(', ');
  return {
    project: clean(form.projectName),
    customer: clean(form.customerName),
    company: clean(form.companyName),
    location,
    preparedBy: clean(form.drawnBy),
    drawingNo: clean(form.drawingNumber) || 'SB-001',
    revision: clean(form.revision) || 'A',
    date: today(),
    dims: `${ftIn(bb.w)} × ${ftIn(bb.h)}${isRect ? '' : ' (overall)'}`,
    area: `${Math.round(area).toLocaleString('en-US')} sq ft`,
    equipCount: items.length,
    doorCount: doors.length - overhead,
    overheadCount: overhead,
    roomCount: rooms.length,
    roomNames: rooms.map((r) => clean(r.label || r.name)).filter(Boolean),
    ceiling: clean(form.ceilingHeight),
    slab: clean(form.concreteThickness),
    condition: clean(form.concreteCondition),
    customNotes: clean(form.notes),
  };
}

// ─── Header band (top of every sheet) ────────────────────────────────────
function header(x, y, w, h, facts, sheetTitle, settings, theme) {
  const out = [];
  out.push(R(x, y, w, h, { fill: theme.headFill, rx: theme.radius }));
  // Brand / logo block
  let bx = x + 12;
  if (settings.logoDataUrl) {
    out.push(`<image x="${f(bx)}" y="${f(y + 5)}" width="${f(h * 1.6)}" height="${f(h - 10)}" preserveAspectRatio="xMidYMid meet" href="${settings.logoDataUrl}"/>`);
    bx += h * 1.6 + 10;
  } else {
    // Small "SB" mark
    out.push(R(bx, y + h / 2 - 9, 18, 18, { fill: C.blue, rx: 3 }));
    out.push(T(bx + 9, y + h / 2 + 3.2, 'SB', { size: 8, bold: true, fill: C.white, anchor: 'middle' }));
    bx += 26;
  }
  out.push(T(bx, y + h / 2 - 4, 'SHOPBUILDER PRO', { size: 6, bold: true, fill: C.sky }));
  out.push(T(bx, y + h / 2 + 8, sheetTitle, { size: 13, bold: true, fill: C.white }));

  // Right side: compact cells (DWG / REV / DATE / SHOP)
  const cells = [
    ['SHOP', facts.dims], ['DATE', facts.date], ['REV', facts.revision], ['DWG NO.', facts.drawingNo],
  ];
  let rx = x + w - 10;
  const cellsOut = [];
  cells.forEach(([lab, val]) => {
    const cw = Math.max(textWidth(val, 8.5, true), textWidth(lab, 5.5, true)) + 14;
    rx -= cw;
    cellsOut.push(L(rx, y + 7, rx, y + h - 7, '#3A5068', 0.6));
    cellsOut.push(T(rx + 7, y + h / 2 - 3, lab, { size: 5.5, bold: true, fill: C.sky }));
    cellsOut.push(T(rx + 7, y + h / 2 + 8, val, { size: 8.5, bold: true, fill: C.white }));
  });
  // Project name fills the middle, trimmed so it never runs into the cells
  const titleEnd = bx + textWidth(sheetTitle, 13, true) + 18;
  const pw = rx - titleEnd - 10;
  if (pw > 40) {
    cellsOut.push(L(titleEnd, y + 7, titleEnd, y + h - 7, '#3A5068', 0.6));
    cellsOut.push(T(titleEnd + 9, y + h / 2 - 3, 'PROJECT', { size: 5.5, bold: true, fill: C.sky }));
    cellsOut.push(T(titleEnd + 9, y + h / 2 + 8, fitText(facts.project || 'Untitled project', 8.5, pw - 9, true),
      { size: 8.5, bold: true, fill: C.white }));
  }
  out.push(...cellsOut);
  return out.join('');
}

// ─── Panel with a title bar (side strip blocks) ──────────────────────────
function panelTitle(x, y, w, title, theme) {
  return R(x, y, w, 13, { fill: theme.tableHead, rx: theme.radius ? 3 : 0 })
    + T(x + 5, y + 9, title, { size: 6.5, bold: true, fill: C.navy });
}

// Compact legend (REF / EQUIPMENT / QTY [/ MODEL]) for the side strip.
// Returns null when it doesn't fit in maxH.
function legendBlock(x, y, w, maxH, rows, showRefs, theme) {
  const rowH = 11;
  const h = 13 + 10 + rows.length * rowH + 3;
  if (h > maxH) return null;
  const out = [panelTitle(x, y, w, showRefs ? 'EQUIPMENT LEGEND' : 'EQUIPMENT', theme)];
  const refW = showRefs ? 30 : 0, qtyW = 22;
  const nameW = w - refW - qtyW - 8;
  let cy = y + 13;
  out.push(T(x + 4, cy + 7, showRefs ? 'REF' : '', { size: 5, bold: true, fill: theme.muted }));
  out.push(T(x + 4 + refW, cy + 7, 'EQUIPMENT', { size: 5, bold: true, fill: theme.muted }));
  out.push(T(x + w - 4, cy + 7, 'QTY', { size: 5, bold: true, fill: theme.muted, anchor: 'end' }));
  cy += 10;
  rows.forEach((r, i) => {
    if (i % 2) out.push(R(x, cy, w, rowH, { fill: theme.zebra }));
    if (showRefs) {
      out.push(`<circle cx="${f(x + 10)}" cy="${f(cy + rowH / 2)}" r="4.6" fill="${C.white}" stroke="${theme.marker}" stroke-width="0.7"/>`);
      out.push(T(x + 10, cy + rowH / 2 + 1.7, r.ref, { size: 4.8, bold: true, fill: theme.markerText, anchor: 'middle' }));
    }
    out.push(T(x + 4 + refW, cy + 7.6, fitText(r.name, 6.5, nameW), { size: 6.5, fill: C.navy }));
    out.push(T(x + w - 4, cy + 7.6, String(r.qty), { size: 6.5, bold: true, fill: C.navy, anchor: 'end' }));
    cy += rowH;
  });
  out.push(L(x, cy + 1, x + w, cy + 1, theme.rule, 0.5));
  return { svg: out.join(''), h };
}

// Notes as numbered paragraphs (disclaimer first). Returns the wrapped
// lines so a caller can split them across sheets.
function noteParagraphs(facts, w) {
  const paras = [DEFAULT_DISCLAIMER, ...VERIFY_NOTES];
  if (facts.customNotes) facts.customNotes.split(/\n+/).map(clean).filter(Boolean).forEach((n) => paras.push(n));
  return paras.map((p, i) => ({ num: `${i + 1}.`, lines: wrapText(p, 6.2, w - 14) }));
}
const NOTE_LH = 8;
const notesHeight = (paras) => 13 + 5 + paras.reduce((s, p) => s + p.lines.length * NOTE_LH + 2.5, 0);
function notesBlock(x, y, w, paras, title, theme) {
  const out = [panelTitle(x, y, w, title, theme)];
  let cy = y + 13 + 5;
  paras.forEach((p) => {
    out.push(T(x + 4, cy + 6, p.num, { size: 6.2, bold: true, fill: C.navy }));
    p.lines.forEach((ln, i) => out.push(T(x + 14, cy + 6 + i * NOTE_LH, ln, { size: 6.2, fill: C.slate })));
    cy += p.lines.length * NOTE_LH + 2.5;
  });
  return { svg: out.join(''), h: cy - y };
}

// ─── Title block (bottom of the side strip) ──────────────────────────────
// Rows of labelled cells; missing values print "Not specified" (key fields)
// or the row is left out (optional fields).
function titleBlockRows(facts, settings) {
  const ns = 'Not specified';
  const rows = [
    [['PROJECT', facts.project || ns, 1]],
    [['CUSTOMER / COMPANY', [facts.customer, facts.company].filter(Boolean).join(' — ') || ns, 1]],
  ];
  if (facts.location) rows.push([['LOCATION', facts.location, 1]]);
  rows.push([['PREPARED BY', facts.preparedBy || ns, 0.6], ['DATE', facts.date, 0.4]]);
  rows.push([['SHOP DIMENSIONS', facts.dims, 0.6], ['FLOOR AREA', facts.area, 0.4]]);
  rows.push([['EQUIPMENT', String(facts.equipCount), 0.25], ['DOORS', String(facts.doorCount), 0.25],
    ['OVERHEAD', String(facts.overheadCount), 0.25], ['ROOMS', String(facts.roomCount), 0.25]]);
  if (settings.showSiteConditions && (facts.ceiling || facts.slab || facts.condition)) {
    rows.push([['CEILING HEIGHT', facts.ceiling || ns, 0.34], ['SLAB THICKNESS', facts.slab || ns, 0.33],
      ['SLAB CONDITION', facts.condition || ns, 0.33]]);
  }
  if (settings.showSignature) rows.push([['APPROVED BY', ' ', 0.6], ['DATE', ' ', 0.4]]);
  return rows;
}
const TB_ROW = 19;
const tbHeight = (rows) => rows.length * TB_ROW + 30;
function titleBlock(x, y, w, facts, settings, pageNo, pageCount, theme) {
  const rows = titleBlockRows(facts, settings);
  const out = [R(x, y, w, tbHeight(rows), { fill: C.white, stroke: theme.frame, sw: 0.9, rx: theme.radius ? 4 : 0 })];
  let cy = y;
  rows.forEach((row, ri) => {
    let cx = x;
    if (ri > 0) out.push(L(x, cy, x + w, cy, theme.rule, 0.5));
    row.forEach(([lab, val, frac], ci) => {
      const cw = w * frac;
      if (ci > 0) out.push(L(cx, cy, cx, cy + TB_ROW, theme.rule, 0.5));
      out.push(T(cx + 4, cy + 6.5, lab, { size: 4.6, bold: true, fill: theme.muted }));
      const muted = val === 'Not specified';
      out.push(T(cx + 4, cy + 15, fitText(val, 6.8, cw - 8, !muted), { size: 6.8, bold: !muted, fill: muted ? '#8C99A6' : C.navy }));
      cx += cw;
    });
    cy += TB_ROW;
  });
  // Bottom row: drawing number (big) | revision | sheet X of Y — on navy
  out.push(R(x, cy, w, 30, { fill: C.navy }));
  const c1 = w * 0.5, c2 = w * 0.2;
  out.push(T(x + 5, cy + 9, 'DRAWING NO.', { size: 4.6, bold: true, fill: C.sky }));
  out.push(T(x + 5, cy + 23, fitText(facts.drawingNo, 12, c1 - 10, true), { size: 12, bold: true, fill: C.white }));
  out.push(L(x + c1, cy + 4, x + c1, cy + 26, '#3A5068', 0.6));
  out.push(T(x + c1 + 5, cy + 9, 'REV', { size: 4.6, bold: true, fill: C.sky }));
  out.push(T(x + c1 + 5, cy + 23, facts.revision, { size: 12, bold: true, fill: C.white }));
  out.push(L(x + c1 + c2, cy + 4, x + c1 + c2, cy + 26, '#3A5068', 0.6));
  out.push(T(x + c1 + c2 + 5, cy + 9, 'SHEET', { size: 4.6, bold: true, fill: C.sky }));
  out.push(T(x + c1 + c2 + 5, cy + 23, `${pageNo} OF ${pageCount}`, { size: 12, bold: true, fill: C.white }));
  return out.join('');
}

// ─── Full equipment schedule table (sheet 2+) ────────────────────────────
// Splits rows across pages; returns per-page chunks of laid-out rows.
function scheduleColumns(w, showRefs) {
  const cols = [
    showRefs && { key: 'ref', title: 'REF', w: 40 },
    { key: 'name', title: 'EQUIPMENT', w: 0.22 },
    { key: 'model', title: 'MODEL', w: 0.2 },
    { key: 'size', title: 'FOOTPRINT (W × D)', w: 0.14 },
    { key: 'qty', title: 'QTY', w: 30 },
    { key: 'notes', title: 'NOTES', w: 0 },          // takes what's left
  ].filter(Boolean);
  const fixed = cols.reduce((s, c) => s + (c.w >= 1 ? c.w : c.w > 0 ? c.w * w : 0), 0);
  cols.forEach((c) => {
    if (c.w >= 1) c.px = c.w;
    else if (c.w > 0) c.px = c.w * w;
    else c.px = Math.max(80, w - fixed);
  });
  return cols;
}
const SCH_FS = 6.8, SCH_LH = 8.6;
function layoutScheduleRows(rows, cols) {
  return rows.map((r) => {
    const cells = {};
    cols.forEach((c) => {
      let v = c.key === 'ref' ? r.refLabel : c.key === 'qty' ? String(r.qty) : r[c.key];
      if (!v) v = c.key === 'model' ? 'Not specified' : c.key === 'notes' ? '—' : '';
      cells[c.key] = (c.key === 'notes' || c.key === 'name' || c.key === 'model')
        ? wrapText(v, SCH_FS, c.px - 8, c.key === 'name')
        : [v];
    });
    const lines = Math.max(...Object.values(cells).map((l) => l.length));
    return { row: r, cells, h: lines * SCH_LH + 6 };
  });
}
function scheduleTable(x, y, w, cols, laid, title, theme, links) {
  const out = [panelTitle(x, y, w, title, theme)];
  let cy = y + 13;
  // Column headings
  let cx = x;
  out.push(R(x, cy, w, 12, { fill: C.white }));
  cols.forEach((c) => { out.push(T(cx + 4, cy + 8.3, c.title, { size: 5.3, bold: true, fill: theme.muted })); cx += c.px; });
  cy += 12;
  out.push(L(x, cy, x + w, cy, C.navy, 0.8));
  laid.forEach((lr, i) => {
    if (i % 2) out.push(R(x, cy, w, lr.h, { fill: theme.zebra }));
    cx = x;
    cols.forEach((c) => {
      const lines = lr.cells[c.key];
      const muted = (c.key === 'model' && !lr.row.model) || (c.key === 'notes' && !lr.row.notes);
      if (c.key === 'ref' && theme.markerR) {
        out.push(T(cx + 4, cy + 3 + SCH_LH - 1.5, lines[0], { size: SCH_FS, bold: true, fill: theme.markerText }));
      } else {
        lines.forEach((ln, li) => out.push(T(cx + 4, cy + 3 + SCH_LH - 1.5 + li * SCH_LH, ln, {
          size: SCH_FS, bold: c.key === 'name' || c.key === 'qty', fill: muted ? '#8C99A6' : (c.key === 'model' && lr.row.link ? C.blue : C.navy),
        })));
      }
      // Clickable model name when we have the manufacturer link
      if (c.key === 'model' && lr.row.link && links) links.push({ x: cx, y: cy, w: c.px, h: lr.h, url: lr.row.link });
      cx += c.px;
    });
    cy += lr.h;
    out.push(L(x, cy, x + w, cy, theme.rule, 0.4));
  });
  // Column rules
  cx = x;
  cols.slice(0, -1).forEach((c) => { cx += c.px; out.push(L(cx, y + 13, cx, cy, theme.rule, 0.4)); });
  out.push(R(x, y, w, cy - y, { stroke: theme.frame, sw: 0.7 }));
  return { svg: out.join(''), h: cy - y };
}

// Black & white: every colour in the sheet → its luminance grey (Rec. 601)
const grey = (r, g, b) => Math.round(0.299 * r + 0.587 * g + 0.114 * b);
function toGreyscale(svg) {
  return svg
    .replace(/#([0-9a-f]{6})\b/gi, (m, h) => {
      const v = grey(parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16));
      const x = v.toString(16).padStart(2, '0');
      return `#${x}${x}${x}`;
    })
    .replace(/rgba\((\d+),(\d+),(\d+),([\d.]+)\)/g, (m, r, g, b, a) => {
      const v = grey(+r, +g, +b);
      return `rgba(${v},${v},${v},${a})`;
    });
}

// ─── Main entry ──────────────────────────────────────────────────────────
// project = { shop, doors, placed, drains, exportForm, summary? {text, flags} }
// Returns { pages: [{ w, h, svg, links }], schedule, settings }
export function buildSheets(project, rawSettings) {
  const settings = resolveSettings(rawSettings);
  const tpl = TEMPLATES[settings.template];
  const theme = tpl.theme;
  const { w: W, h: H } = PAPER_SIZES[settings.paper];
  const facts = projectFacts(project);
  const schedule = buildEquipmentSchedule(project.placed);
  const hasRows = settings.showSchedule && schedule.rows.length > 0;

  // Frame geometry (same on every sheet)
  const M = W >= 1200 ? 24 : 18;                      // outer margin
  const HEAD_H = 38;
  const GAP = 8;
  const stripW = Math.round(Math.min(330, Math.max(196, W * 0.25)));
  const innerX = M, innerY = M + HEAD_H + GAP, innerW = W - M * 2, innerH = H - innerY - M;
  const stripX = W - M - stripW;
  const bodyW = innerW - stripW - GAP;

  // Side strip: title block pinned to the bottom, other blocks stack on top
  const tbRows = titleBlockRows(facts, settings);
  const tbH = settings.showTitleBlock ? tbHeight(tbRows) : 0;
  const stripFreeH = innerH - (tbH ? tbH + GAP : 0);

  // ── Sheet 1 strip contents ──
  const strip1 = [];
  let sy = innerY;
  let legendShown = false;
  if (hasRows) {
    const lg = legendBlock(stripX, sy, stripW, stripFreeH, schedule.rows, settings.showRefs, theme);
    if (lg) { strip1.push(lg.svg); sy += lg.h + GAP; legendShown = true; }
  }
  // When the legend doesn't fit, point to the schedule sheet instead
  let pointerAt = null;
  if (hasRows && !legendShown) { pointerAt = sy; sy += 18; }
  const notesParas = settings.showNotes ? noteParagraphs(facts, stripW) : [];
  let notesOnSheet1 = false;
  if (notesParas.length && notesHeight(notesParas) <= innerY + stripFreeH - sy) {
    const nb = notesBlock(stripX, sy, stripW, notesParas, 'GENERAL NOTES', theme);
    strip1.push(nb.svg); sy += nb.h + GAP; notesOnSheet1 = true;
  }

  // ── Later sheets: full schedule, then leftover notes (both can split) ──
  const later = [];       // [{ blocks: [svg], links: [] }]
  const needSchedulePage = hasRows;     // the full table always gets its own sheet
  if (needSchedulePage || (notesParas.length && !notesOnSheet1)) {
    const cols = scheduleColumns(bodyW, settings.showRefs);
    let laid = hasRows ? layoutScheduleRows(schedule.rows, cols) : [];
    let page = null;
    let py = 0;
    const newPage = () => { page = { blocks: [], links: [] }; later.push(page); py = innerY; };
    newPage();
    let part = 0;
    while (laid.length) {
      // Fit as many rows as the page has room for (at least one)
      let used = 13 + 12, n = 0;
      while (n < laid.length && used + laid[n].h <= innerH - (py - innerY)) { used += laid[n].h; n += 1; }
      if (n === 0 && py > innerY) { newPage(); continue; }
      n = Math.max(1, n);
      const title = part === 0 ? 'EQUIPMENT SCHEDULE' : 'EQUIPMENT SCHEDULE (CONTINUED)';
      const tb = scheduleTable(innerX, py, bodyW, cols, laid.slice(0, n), title, theme, page.links);
      page.blocks.push(tb.svg);
      py += tb.h + GAP * 2;
      laid = laid.slice(n);
      part += 1;
      if (laid.length) newPage();
    }
    if (notesParas.length && !notesOnSheet1) {
      // Notes use the full body width on these sheets → re-wrap
      let paras = noteParagraphs(facts, bodyW);
      let first = true;
      while (paras.length) {
        let n = 0, used = 18;
        while (n < paras.length && used + paras[n].lines.length * NOTE_LH + 2.5 <= innerH - (py - innerY)) {
          used += paras[n].lines.length * NOTE_LH + 2.5; n += 1;
        }
        if (n === 0) {
          if (py > innerY) { newPage(); continue; }
          n = 1;       // a single note taller than a page: print it anyway
        }
        const nb = notesBlock(innerX, py, bodyW, paras.slice(0, n), first ? 'GENERAL NOTES' : 'GENERAL NOTES (CONTINUED)', theme);
        page.blocks.push(nb.svg);
        py += nb.h + GAP * 2;
        paras = paras.slice(n);
        first = false;
        if (paras.length) newPage();
      }
    }
  }

  // ── Optional order summary (AI write-up + verified site flags) ──
  const summary = project.summary;
  if (summary && (clean(summary.text) || (summary.flags || []).length)) {
    const blocks = [];
    if ((summary.flags || []).length) {
      blocks.push({ title: 'THINGS TO VERIFY BEFORE INSTALLATION', paras: summary.flags.map((fl) => ({ num: '•', lines: wrapText(fl, 6.2, bodyW - 14) })) });
    }
    if (clean(summary.text)) {
      blocks.push({ title: 'ORDER SUMMARY', paras: clean(summary.text).split(/\n\s*\n|\n/).map(clean).filter(Boolean)
        .map((t) => ({ num: '', lines: wrapText(t, 6.2, bodyW - 14) })) });
    }
    let page = { title: 'ORDER SUMMARY', blocks: [], links: [] };
    later.push(page);
    let py = innerY;
    blocks.forEach((b) => {
      let paras = b.paras, first = true;
      while (paras.length) {
        let n = 0, used = 18;
        while (n < paras.length && used + paras[n].lines.length * NOTE_LH + 2.5 <= innerH - (py - innerY)) {
          used += paras[n].lines.length * NOTE_LH + 2.5; n += 1;
        }
        if (n === 0 && py > innerY) { page = { title: 'ORDER SUMMARY', blocks: [], links: [] }; later.push(page); py = innerY; continue; }
        n = Math.max(1, n);
        const nb = notesBlock(innerX, py, bodyW, paras.slice(0, n), first ? b.title : `${b.title} (CONTINUED)`, theme);
        page.blocks.push(nb.svg);
        py += nb.h + GAP * 2;
        paras = paras.slice(n);
        first = false;
      }
    });
  }
  const pageCount = 1 + later.length;

  // ── Shared frame for a sheet ──
  const frame = (pageNo, sheetTitle, bodySvg, stripSvg) => {
    const parts = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`,
      R(0, 0, W, H, { fill: theme.paper }),
      // Thin outer border like a drafting sheet
      R(M / 2, M / 2, W - M, H - M, { stroke: theme.frame, sw: 0.8, rx: theme.radius }),
      header(M, M, innerW, HEAD_H, facts, sheetTitle, settings, theme),
      bodySvg,
      stripSvg,
    ];
    if (settings.showTitleBlock) parts.push(titleBlock(stripX, innerY + innerH - tbH, stripW, facts, settings, pageNo, pageCount, theme));
    parts.push('</svg>');
    return parts.join('\n');
  };

  // ── Sheet 1: the plan ──
  const planBox = { x: innerX, y: innerY, w: bodyW, h: innerH };
  const planOpts = {
    showGrid: settings.showGrid,
    showLabels: settings.showLabels,
    showRefs: settings.showRefs && hasRows,
    showDimensions: settings.showDimensions,
    showRoomDimensions: settings.showRoomDimensions,
    showClearance: settings.showClearance,
    showDoorTags: true,
    showNorth: true,
    showScaleBar: true,
  };
  const plan = renderPlan({
    shop: project.shop, doors: project.doors, placed: project.placed, drains: project.drains,
    refByUid: schedule.refByUid, opts: planOpts, theme, box: planBox,
  });
  const planFrame = R(planBox.x, planBox.y, planBox.w, planBox.h,
    { fill: theme.radius ? '#FBFCFE' : 'none', stroke: theme.frame, sw: theme.radius ? 0.6 : 0.9, rx: theme.radius });
  // A pointer when the legend didn't fit on sheet 1
  let pointer = '';
  if (pointerAt != null) {
    pointer = T(stripX + 4, pointerAt + 10, `Equipment schedule: see sheet 2 of ${pageCount}`, { size: 6.5, bold: true, fill: C.blue });
  }
  const pages = [{
    w: W, h: H, links: [],
    svg: frame(1, 'EQUIPMENT LAYOUT PLAN', planFrame + plan.svg, strip1.join('') + pointer),
  }];
  later.forEach((pg, i) => {
    pages.push({ w: W, h: H, links: pg.links, svg: frame(i + 2, pg.title || 'EQUIPMENT SCHEDULE & NOTES', pg.blocks.join(''), '') });
  });
  if (settings.greyscale) pages.forEach((pg) => { pg.svg = toGreyscale(pg.svg); });

  return { pages, schedule, settings, scale: plan.scale, facts };
}
